import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AddMemoryModal from './AddMemoryModal';
import MemoryService from './MemoryService';
import './App.css';

function App() {
  const [memories, setMemories] = useState([]);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    // Load memories from API
    const loadMemories = async () => {
      try {
        const loadedMemories = await MemoryService.loadMemories();
        setMemories(loadedMemories);
      } catch (error) {
        console.error('Error loading memories:', error);
        // Start with empty array - no default memories
        setMemories([]);
      }
    };

    loadMemories();
  }, []);

  // Load images and calculate their dimensions
  useEffect(() => {
    const loadMemoriesWithDimensions = async () => {
      const memoriesWithDimensions = await Promise.all(
        memories.map(memory => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              const aspectRatio = img.naturalHeight / img.naturalWidth;
              const baseHeight = 200;
              const height = Math.max(baseHeight * aspectRatio, 150);
              const gridRowSpan = Math.ceil(height / 10);
              
              resolve({
                ...memory,
                aspectRatio,
                gridRowSpan,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight
              });
            };
            img.onerror = () => {
              // If image fails to load, use default dimensions
              resolve({
                ...memory,
                aspectRatio: 1,
                gridRowSpan: 20,
                naturalWidth: 300,
                naturalHeight: 300
              });
            };
            
            // Handle different image sources
            if (memory.imageUrl) {
              img.src = memory.imageUrl; // S3 URL from API
            } else if (memory.imageData) {
              img.src = memory.imageData; // Base64 data
            } else if (memory.file && memory.file instanceof File) {
              img.src = URL.createObjectURL(memory.file); // File object
            } else {
              img.src = `/photos/${memory.filename}`; // Existing photos
            }
          });
        })
      );
      setMemories(memoriesWithDimensions);
    };

    if (memories.length > 0) {
      loadMemoriesWithDimensions();
    }
  }, [memories.length]);

  const addMemory = async (newMemoryData, password) => {
    try {
      const newMemory = await MemoryService.addMemory(newMemoryData, password);
      
      // Update local state
      const updatedMemories = [...memories, newMemory];
      setMemories(updatedMemories);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding memory:', error);
      throw error; // Re-throw so modal can handle it
    }
  };

  const openModal = (memory) => {
    setSelectedMemory(memory);
  };

  const closeModal = () => {
    setSelectedMemory(null);
  };

  const getImageSrc = (memory) => {
    if (memory.imageUrl) {
      return memory.imageUrl; // Use S3 URL from API
    }
    if (memory.imageData) {
      return memory.imageData; // Use base64 data
    }
    if (memory.file && memory.file instanceof File) {
      return URL.createObjectURL(memory.file); // Only if it's actually a File object
    }
    return `/photos/${memory.filename}`; // Fallback to existing photos
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>The SPAM Capsule 📦</h1>
        <p>A gallery for all of our favorite memories and moments</p>
        <button 
          className="add-memory-btn"
          onClick={() => setShowAddModal(true)}
        >
          + Add New Memory
        </button>
      </header>

      <main className="photo-gallery">
        {memories.map((memory) => (
          <div 
            key={memory.id} 
            className="photo-item" 
            style={{
              gridRowEnd: `span ${memory.gridRowSpan || 20}`
            }}
          >
            <img
              src={getImageSrc(memory)}
              alt={memory.title}
              className="photo-thumbnail"
              onClick={() => openModal(memory)}
            />
            <div className="photo-overlay">
              <span>{memory.title}</span>
              <div className="photo-actions">
                <button 
                  onClick={() => openModal(memory)}
                  className="quick-view-btn"
                >
                  Quick View
                </button>
                <Link 
                  to={`/memory/${memory.id}`}
                  className="full-page-btn"
                >
                  Full Page
                </Link>
              </div>
            </div>
          </div>
        ))}
      </main>

      {selectedMemory && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closeModal}>×</button>
            <img
              src={getImageSrc(selectedMemory)}
              alt={selectedMemory.title}
              className="modal-image"
            />
            <div className="modal-footer">
              <h3>{selectedMemory.title}</h3>
              <Link 
                to={`/memory/${selectedMemory.id}`}
                className="modal-full-page-link"
                onClick={closeModal}
              >
                View Full Page →
              </Link>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddMemoryModal 
          onClose={() => setShowAddModal(false)}
          onAdd={addMemory}
        />
      )}

      <footer className="App-footer">
        <p>Made with ❤️ for all our adventures together</p>
      </footer>
    </div>
  );
}

export default App;
